import { feedbackStore } from '../data/feedback-store.js';

export function renderGettingStartedView() {
  const state = feedbackStore.getState();
  const profile = state.profile || {};
  const stats = feedbackStore.getProgressStats();
  const hasProfile = Boolean(profile.name && profile.site && profile.role);

  return `
    <section class="view-section" id="view-getting-started">
      <!-- Hero Header Banner -->
      <div class="hero-card" style="background: linear-gradient(135deg, rgba(30, 27, 75, 0.85) 0%, rgba(15, 23, 42, 0.95) 100%); border-color: rgba(99, 102, 241, 0.4); padding: var(--space-2xl) var(--space-3xl);">
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-sm); flex-wrap: wrap;">
          <span class="hero-tag" style="margin-bottom: 0;">Getting Started Guide</span>
          <span class="badge" style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.3); font-weight: 700;">
            2026 Brand Refresh Proposal
          </span>
          <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">
            Urban Village Church • Chicago
          </span>
        </div>

        <h1 style="font-size: clamp(2rem, 4vw, 2.75rem); line-height: 1.15; margin: var(--space-sm) 0 var(--space-md) 0; font-weight: 900; letter-spacing: -0.02em;">
          Welcome to the UVC Brand Proposal &amp; Review Hub
        </h1>
        
        <p style="max-width: 860px; font-size: var(--text-lg); color: #cbd5e1; margin-bottom: var(--space-xl); line-height: 1.6;">
          An interactive evaluation platform for UVC pastoral staff, lay leadership, site coordinators, and community members. Explore demographic research, test three divergent creative brand directions in real time, and guide the next decade of our bold, inclusive, Jesus-rooted community.
        </p>

        <!-- Quick Meta Info Badges -->
        <div style="display: flex; gap: var(--space-md); flex-wrap: wrap; margin-bottom: var(--space-xl); font-size: var(--text-xs); color: var(--text-secondary);">
          <div style="display: flex; align-items: center; gap: 6px; background: var(--bg-surface); padding: 6px 12px; border-radius: var(--radius-full); border: 1px solid var(--border-subtle);">
            <span>⏱️</span>
            <strong style="color: var(--text-primary);">Est. Time:</strong> 10–15 minutes
          </div>
          <div style="display: flex; align-items: center; gap: 6px; background: var(--bg-surface); padding: 6px 12px; border-radius: var(--radius-full); border: 1px solid var(--border-subtle);">
            <span>💾</span>
            <strong style="color: var(--text-primary);">Auto-Saved:</strong> Stored locally in browser
          </div>
          <div style="display: flex; align-items: center; gap: 6px; background: var(--bg-surface); padding: 6px 12px; border-radius: var(--radius-full); border: 1px solid var(--border-subtle);">
            <span>🛡️</span>
            <strong style="color: var(--text-primary);">Confidential:</strong> Informs leadership decisions
          </div>
          <div style="display: flex; align-items: center; gap: 6px; background: var(--bg-surface); padding: 6px 12px; border-radius: var(--radius-full); border: 1px solid var(--border-subtle);">
            <span>📍</span>
            <strong style="color: var(--text-primary);">5 Sites:</strong> HPW • RV • WP • RF • Online
          </div>
        </div>

        <!-- Dynamic Reviewer Quick Setup Card -->
        <div class="reviewer-onboarding-box" style="background: rgba(15, 23, 42, 0.75); border: 1px solid ${hasProfile ? 'rgba(16, 185, 129, 0.4)' : 'rgba(245, 158, 11, 0.5)'}; border-radius: var(--radius-lg); padding: var(--space-lg); margin-bottom: var(--space-xl);">
          <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: center; gap: var(--space-md);">
              <div style="width: 44px; height: 44px; border-radius: 50%; background: ${hasProfile ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;">
                ${hasProfile ? '👤' : '⚠️'}
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: 2px;">
                  <strong style="color: var(--text-primary); font-size: var(--text-base);">
                    ${hasProfile ? `Reviewing as: ${profile.name}` : 'Step 0: Set Your Reviewer Profile'}
                  </strong>
                  <span class="badge ${hasProfile ? 'badge-success' : ''}" style="${hasProfile ? '' : 'background: #f59e0b; color: #000; font-weight: 700;'}">
                    ${hasProfile ? `${profile.role} • ${profile.site}` : 'Required for Attribution'}
                  </span>
                </div>
                <p style="margin: 0; font-size: var(--text-xs); color: var(--text-secondary); max-width: 600px;">
                  ${hasProfile 
                    ? `Your feedback is linked to ${profile.site}. Progress: ${stats.completedCount} of ${stats.totalCount} sections evaluated (${stats.percentage}% complete).`
                    : 'Please select your home site and church role so your insights can be accurately weighed during site-by-site leadership synthesis.'}
                </p>
              </div>
            </div>

            <div style="display: flex; gap: var(--space-sm);">
              <button 
                type="button" 
                class="btn" 
                id="btn-open-profile"
                style="background: ${hasProfile ? 'var(--bg-surface-elevated)' : 'var(--accent-gold)'}; color: ${hasProfile ? 'var(--text-primary)' : '#000000'}; font-weight: 700; padding: 8px 18px; border-radius: var(--radius-md);"
              >
                ${hasProfile ? '✏️ Edit Profile' : '👉 Set Reviewer Profile Now'}
              </button>
            </div>
          </div>

          ${hasProfile ? `
            <div style="margin-top: var(--space-md); padding-top: var(--space-sm); border-top: 1px solid var(--border-subtle); display: flex; align-items: center; gap: var(--space-md);">
              <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">Evaluation Progress:</span>
              <div style="flex-grow: 1; height: 8px; background: rgba(255,255,255,0.08); border-radius: var(--radius-full); overflow: hidden;">
                <div style="width: ${stats.percentage}%; height: 100%; background: linear-gradient(90deg, var(--accent-primary), #10b981); transition: width 0.3s ease;"></div>
              </div>
              <span class="mono" style="font-size: 11px; color: #10b981; font-weight: 700;">${stats.percentage}%</span>
            </div>
          ` : ''}
        </div>

        <!-- Primary Action Buttons -->
        <div style="display: flex; gap: var(--space-md); flex-wrap: wrap;">
          <button class="nav-item-btn active" data-tab="overview" style="width: auto; padding: 12px 24px; background: var(--accent-primary); color: white; border-radius: var(--radius-full); font-weight: 700; box-shadow: var(--shadow-md);">
            🚀 Start Guided Review (Step 1) →
          </button>
          <button class="nav-item-btn" data-tab="concepts" style="width: auto; padding: 12px 24px; background: var(--bg-surface-elevated); border-radius: var(--radius-full); font-weight: 700; border: 1px solid var(--border-strong);">
            ⚡ Jump Directly to 3 Brand Concepts
          </button>
          <button class="nav-item-btn" data-tab="ballot" style="width: auto; padding: 12px 24px; background: rgba(99, 102, 241, 0.15); border: 1px solid var(--border-accent); border-radius: var(--radius-full); font-weight: 700; color: #a5b4fc;">
            🗳️ View Final Ballot
          </button>
        </div>
      </div>

      <!-- 4 Core Pillars Section -->

      <!-- Pillar 1: What This Site Is -->
      <div style="margin-bottom: var(--space-3xl);">
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-sm);">
          <span class="mono" style="color: var(--accent-primary); font-weight: 800; font-size: var(--text-sm);">PILLAR 01</span>
          <span style="color: var(--text-tertiary);">•</span>
          <span style="font-size: var(--text-sm); color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700;">Context &amp; Purpose</span>
        </div>
        <h2 style="font-size: var(--text-2xl); font-weight: 800; margin: 0 0 var(--space-md) 0;">What This Site Is</h2>
        <p style="color: var(--text-secondary); max-width: 800px; margin-bottom: var(--space-lg); font-size: var(--text-base);">
          Urban Village Church has entered a strategic inflection point. This platform presents the full brand refresh proposal developed across research, theological positioning, and multi-site creative design.
        </p>

        <div class="grid-3">
          <div class="card" style="display: flex; flex-direction: column; justify-content: space-between;">
            <div>
              <div style="font-size: 28px; margin-bottom: var(--space-sm);">🏛️</div>
              <h3 style="font-size: var(--text-lg); margin-bottom: var(--space-xs); color: var(--text-primary);">A 15-Year Milestone</h3>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6;">
                Founded in 2010, UVC has grown from a single gathering into a vibrant 5-site network across Chicago (Wicker Park, Ravenswood, Hyde Park/Woodlawn, River Forest, and Online). This refresh modernizes our public presence for the next generation.
              </p>
            </div>
            <div style="padding-top: var(--space-sm); border-top: 1px solid var(--border-subtle); margin-top: var(--space-sm);">
              <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">5 Chicago Sites • 1 Shared Gospel</span>
            </div>
          </div>

          <div class="card" style="display: flex; flex-direction: column; justify-content: space-between;">
            <div>
              <div style="font-size: 28px; margin-bottom: var(--space-sm);">🎯</div>
              <h3 style="font-size: var(--text-lg); margin-bottom: var(--space-xs); color: var(--text-primary);">Why a Brand Refresh?</h3>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6;">
                Modern Chicagoans face spiritual trauma, institutional skepticism, and brand confusion. A unified visual and messaging system eliminates graphic fragmentation, elevates accessibility, and clearly invites deconstructing seekers home.
              </p>
            </div>
            <div style="padding-top: var(--space-sm); border-top: 1px solid var(--border-subtle); margin-top: var(--space-sm);">
              <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">Reaching "Nones" &amp; Exvangelicals</span>
            </div>
          </div>

          <div class="card" style="display: flex; flex-direction: column; justify-content: space-between;">
            <div>
              <div style="font-size: 28px; margin-bottom: var(--space-sm);">🔥</div>
              <h3 style="font-size: var(--text-lg); margin-bottom: var(--space-xs); color: var(--text-primary);">The 3 Invariable Pillars</h3>
              <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6;">
                All three concepts are unshakeably rooted in UVC’s core DNA: <strong style="color: #f8fafc;">Bold</strong> (prophetic justice &amp; anti-racism), <strong style="color: #f8fafc;">Inclusive</strong> (radical LGBTQIA+ celebration), and <strong style="color: #f8fafc;">Relevant</strong> (honest urban theology).
              </p>
            </div>
            <div style="padding-top: var(--space-sm); border-top: 1px solid var(--border-subtle); margin-top: var(--space-sm);">
              <span class="mono" style="font-size: 11px; color: var(--accent-primary); font-weight: 700;">Bold • Inclusive • Relevant</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pillar 2: What It Does & Interactive Tools -->
      <div style="margin-bottom: var(--space-3xl);">
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-sm);">
          <span class="mono" style="color: var(--accent-primary); font-weight: 800; font-size: var(--text-sm);">PILLAR 02</span>
          <span style="color: var(--text-tertiary);">•</span>
          <span style="font-size: var(--text-sm); color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700;">Live Interactive Capabilities</span>
        </div>
        <h2 style="font-size: var(--text-2xl); font-weight: 800; margin: 0 0 var(--space-md) 0;">What This Platform Does</h2>
        <p style="color: var(--text-secondary); max-width: 800px; margin-bottom: var(--space-lg); font-size: var(--text-base);">
          This is not a static PDF or slide deck. It is an interactive design laboratory built to give leaders hands-on testing tools:
        </p>

        <div class="grid-2" style="margin-bottom: var(--space-lg);">
          <div class="card" style="background: linear-gradient(180deg, var(--bg-surface) 0%, rgba(30, 27, 75, 0.2) 100%);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="font-size: 32px; background: rgba(99, 102, 241, 0.15); padding: 12px; border-radius: var(--radius-md); border: 1px solid var(--border-accent);">🎨</div>
              <div>
                <h3 style="font-size: var(--text-base); margin-bottom: 4px; color: var(--text-primary);">Live Concept Theme Engine</h3>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-sm); line-height: 1.5;">
                  Experience how each concept transforms our digital environment. On any concept page, click <strong style="color: var(--accent-primary);">"Apply Theme to UI"</strong> to instantly restyle the entire app with that concept's custom color palette, borders, and tokens.
                </p>
                <button class="btn btn-secondary btn-sm" data-tab="concepts" style="font-size: 11px;">
                  Try Theme Switcher in Concepts &rarr;
                </button>
              </div>
            </div>
          </div>

          <div class="card" style="background: linear-gradient(180deg, var(--bg-surface) 0%, rgba(16, 185, 129, 0.1) 100%);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="font-size: 32px; background: rgba(16, 185, 129, 0.15); padding: 12px; border-radius: var(--radius-md); border: 1px solid rgba(16, 185, 129, 0.3);">👁️</div>
              <div>
                <h3 style="font-size: var(--text-base); margin-bottom: 4px; color: var(--text-primary);">WCAG 2.1 Contrast Checker</h3>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-sm); line-height: 1.5;">
                  Radical inclusivity demands radical visual accessibility. Use the interactive color contrast calculator in each concept tab to test foreground/background combinations against AAA and AA legibility criteria.
                </p>
                <button class="btn btn-secondary btn-sm" data-tab="concepts" style="font-size: 11px;">
                  Inspect Color Accessibility &rarr;
                </button>
              </div>
            </div>
          </div>

          <div class="card" style="background: linear-gradient(180deg, var(--bg-surface) 0%, rgba(245, 158, 11, 0.1) 100%);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="font-size: 32px; background: rgba(245, 158, 11, 0.15); padding: 12px; border-radius: var(--radius-md); border: 1px solid rgba(245, 158, 11, 0.3);">🖼️</div>
              <div>
                <h3 style="font-size: var(--text-base); margin-bottom: 4px; color: var(--text-primary);">Full-Resolution Collateral Lightbox</h3>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-sm); line-height: 1.5;">
                  Click on any mockup—street sidewalk A-frames, worship slides, tote bags, social ads, or sermon series artwork—to open a high-resolution lightbox with detailed design notes and micro-reactions.
                </p>
                <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">7 Mockup Types Per Concept</span>
              </div>
            </div>
          </div>

          <div class="card" style="background: linear-gradient(180deg, var(--bg-surface) 0%, rgba(6, 182, 212, 0.1) 100%);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="font-size: 32px; background: rgba(6, 182, 212, 0.15); padding: 12px; border-radius: var(--radius-md); border: 1px solid rgba(6, 182, 212, 0.3);">📊</div>
              <div>
                <h3 style="font-size: var(--text-base); margin-bottom: 4px; color: var(--text-primary);">Comparative Decision Matrix</h3>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-sm); line-height: 1.5;">
                  Evaluate all 3 concepts side-by-side across 6 strategic criteria: Theological Resonance, Inclusivity Visibility, Multi-Site Flexibility, Deconstructing Appeal, Digital Agility, and Production Simplicity.
                </p>
                <button class="btn btn-secondary btn-sm" data-tab="comparison" style="font-size: 11px;">
                  Open Decision Matrix &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pillar 3: How Feedback Works & Auto-Saving -->
      <div style="margin-bottom: var(--space-3xl);">
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-sm);">
          <span class="mono" style="color: var(--accent-primary); font-weight: 800; font-size: var(--text-sm);">PILLAR 03</span>
          <span style="color: var(--text-tertiary);">•</span>
          <span style="font-size: var(--text-sm); color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700;">Feedback &amp; Storage Architecture</span>
        </div>
        <h2 style="font-size: var(--text-2xl); font-weight: 800; margin: 0 0 var(--space-md) 0;">How Your Feedback Works</h2>
        <p style="color: var(--text-secondary); max-width: 800px; margin-bottom: var(--space-lg); font-size: var(--text-base);">
          We designed the feedback system to be effortless, flexible, and completely safe from accidental data loss:
        </p>

        <div class="grid-4">
          <div class="card">
            <div style="font-size: 24px; margin-bottom: 8px;">📝</div>
            <h4 style="font-size: var(--text-sm); margin-bottom: 6px; color: var(--text-primary);">1. Inline Ratings</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Score each concept (1–5 scale) on Bold, Inclusive, Relevant, and Site Fit right where you see the designs.
            </p>
          </div>

          <div class="card">
            <div style="font-size: 24px; margin-bottom: 8px;">❤️</div>
            <h4 style="font-size: var(--text-sm); margin-bottom: 6px; color: var(--text-primary);">2. Micro-Reactions</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Tap reactions (🔥, ❤️, ✨, 💡, 🤔) on individual mockups to indicate exact assets you love or want refined.
            </p>
          </div>

          <div class="card">
            <div style="font-size: 24px; margin-bottom: 8px;">💾</div>
            <h4 style="font-size: var(--text-sm); margin-bottom: 6px; color: var(--text-primary);">3. Instant Auto-Save</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Every keystroke and click saves directly into your browser's persistent storage. Close your tab anytime without worry.
            </p>
          </div>

          <div class="card">
            <div style="font-size: 24px; margin-bottom: 8px;">🗳️</div>
            <h4 style="font-size: var(--text-sm); margin-bottom: 6px; color: var(--text-primary);">4. Ballot &amp; Export</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Rank your 1st, 2nd, and 3rd choices, then copy your formatted summary or download JSON for the Pastoral Team.
            </p>
          </div>
        </div>
      </div>

      <!-- Pillar 4: Step-by-Step Instructions & Roadmap (5-Step Review Journey) -->
      <div style="margin-bottom: var(--space-3xl);">
        <div style="display: flex; align-items: center; gap: var(--space-sm); margin-bottom: var(--space-sm);">
          <span class="mono" style="color: var(--accent-primary); font-weight: 800; font-size: var(--text-sm);">PILLAR 04</span>
          <span style="color: var(--text-tertiary);">•</span>
          <span style="font-size: var(--text-sm); color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700;">Step-by-Step Instructions</span>
        </div>
        <h2 style="font-size: var(--text-2xl); font-weight: 800; margin: 0 0 var(--space-md) 0;">5 Steps to Complete Your Review</h2>
        <p style="color: var(--text-secondary); max-width: 800px; margin-bottom: var(--space-xl); font-size: var(--text-base);">
          Follow these structured steps for a comprehensive evaluation. You can complete all steps at once (10–15 mins) or review module by module.
        </p>

        <div style="display: flex; flex-direction: column; gap: var(--space-lg);">
          <!-- Step 1 -->
          <div class="card" style="border-left: 4px solid #f59e0b; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(245, 158, 11, 0.15); color: #f59e0b; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0;">
                1
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm);">
                  <h3 style="font-size: var(--text-base); margin: 0; color: var(--text-primary);">Set Your Reviewer Identity</h3>
                  <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">⏱️ 1 Min</span>
                </div>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 4px 0 0 0; line-height: 1.5; max-width: 650px;">
                  Ensure your feedback is categorized by your UVC role (Staff, Lay Leader, Member, Attender, Friend) and home site (HPW, Ravenswood, Wicker Park, River Forest, Online).
                </p>
              </div>
            </div>
            <button class="btn btn-secondary btn-sm" id="btn-prompt-set-profile" style="font-weight: 700;">
              ${hasProfile ? '✓ Profile Configured (Edit)' : '👉 Set Profile Now'}
            </button>
          </div>

          <!-- Step 2 -->
          <div class="card" style="border-left: 4px solid var(--accent-primary); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(99, 102, 241, 0.15); color: var(--accent-primary); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0;">
                2
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm);">
                  <h3 style="font-size: var(--text-base); margin: 0; color: var(--text-primary);">Understand the Strategic Foundation</h3>
                  <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">⏱️ 3 Mins</span>
                </div>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 4px 0 0 0; line-height: 1.5; max-width: 650px;">
                  Explore our comprehensive touchpoint audit (01), neighborhood demographic research (02), and core brand strategy framework (03).
                </p>
              </div>
            </div>
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              <button class="btn btn-secondary btn-sm" data-tab="assessment" style="font-size: 11px;">01. Audit</button>
              <button class="btn btn-secondary btn-sm" data-tab="demographics" style="font-size: 11px;">02. Demographics</button>
              <button class="btn btn-secondary btn-sm" data-tab="strategy" style="font-size: 11px;">03. Strategy</button>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="card" style="border-left: 4px solid #10b981; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(16, 185, 129, 0.15); color: #10b981; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0;">
                3
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm);">
                  <h3 style="font-size: var(--text-base); margin: 0; color: var(--text-primary);">Deep-Dive into the 3 Brand Concepts</h3>
                  <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">⏱️ 5 Mins</span>
                  <span class="badge" style="background: rgba(16, 185, 129, 0.2); color: #34d399; font-size: 10px;">Core Creative</span>
                </div>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 4px 0 0 0; line-height: 1.5; max-width: 650px;">
                  Review the 3 divergent creative directions: <strong>Concept 01: Bold Activism</strong> (prophetic street energy), <strong>Concept 02: Grounded Sanctuary</strong> (warm terracotta &amp; contemplative rest), and <strong>Concept 03: Neo-Liturgical</strong> (sacred geometry &amp; deep indigo). Test themes and score rubric dimensions.
                </p>
              </div>
            </div>
            <button class="btn btn-secondary btn-sm" data-tab="concepts" style="background: rgba(16, 185, 129, 0.15); color: #34d399; border-color: rgba(16, 185, 129, 0.4); font-weight: 700;">
              🎨 Explore 3 Concepts &rarr;
            </button>
          </div>

          <!-- Step 4 -->
          <div class="card" style="border-left: 4px solid #06b6d4; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(6, 182, 212, 0.15); color: #06b6d4; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0;">
                4
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm);">
                  <h3 style="font-size: var(--text-base); margin: 0; color: var(--text-primary);">Compare on the Decision Matrix</h3>
                  <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">⏱️ 2 Mins</span>
                </div>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 4px 0 0 0; line-height: 1.5; max-width: 650px;">
                  Examine side-by-side trade-offs across theology, multi-site implementation, youth appeal, digital engagement, and execution budgets.
                </p>
              </div>
            </div>
            <button class="btn btn-secondary btn-sm" data-tab="comparison" style="font-size: 11px;">
              📊 Open Matrix &rarr;
            </button>
          </div>

          <!-- Step 5 -->
          <div class="card" style="border-left: 4px solid #818cf8; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-md);">
            <div style="display: flex; align-items: flex-start; gap: var(--space-md);">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: rgba(129, 140, 248, 0.15); color: #818cf8; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0;">
                5
              </div>
              <div>
                <div style="display: flex; align-items: center; gap: var(--space-sm);">
                  <h3 style="font-size: var(--text-base); margin: 0; color: var(--text-primary);">Cast Your Final Ballot &amp; Submit</h3>
                  <span class="mono" style="font-size: 10px; color: var(--text-tertiary);">⏱️ 3 Mins</span>
                  <span class="badge badge-success" style="font-size: 10px;">Final Step</span>
                </div>
                <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 4px 0 0 0; line-height: 1.5; max-width: 650px;">
                  Rank your 1st, 2nd, and 3rd choices via drag-and-drop or rank buttons, choose priority collateral items, and submit advice for pastoral leadership.
                </p>
              </div>
            </div>
            <button class="btn btn-primary btn-sm" data-tab="ballot" style="background: var(--accent-primary); color: #FFFFFF; font-weight: 700;">
              🗳️ Go to Final Ballot &rarr;
            </button>
          </div>
        </div>
      </div>

      <!-- FAQ & Review Guidance Section -->
      <div class="card" style="background: var(--bg-surface-elevated); border: 1px solid var(--border-strong); padding: var(--space-2xl); margin-bottom: var(--space-2xl);">
        <h3 style="font-size: var(--text-xl); margin-bottom: var(--space-md); color: var(--text-primary);">
          💡 Reviewer Tips &amp; Frequently Asked Questions
        </h3>
        
        <div class="grid-3" style="gap: var(--space-lg);">
          <div>
            <h4 style="font-size: var(--text-sm); color: #a5b4fc; margin-bottom: 4px;">Who is this feedback for?</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              All responses are synthesized into an executive report for the UVC Pastoral Staff, Design Task Force, and Church Council to finalize the brand refresh.
            </p>
          </div>

          <div>
            <h4 style="font-size: var(--text-sm); color: #a5b4fc; margin-bottom: 4px;">Can I change my answers?</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Yes! All scores, reactions, rankings, and notes remain live and editable in your browser until you are completely ready to export.
            </p>
          </div>

          <div>
            <h4 style="font-size: var(--text-sm); color: #a5b4fc; margin-bottom: 4px;">Can we combine concepts?</h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; margin: 0;">
              Absolutely. If you love Concept 1’s bold messaging with Concept 2’s warm sanctuary color palette, specify this in your Ballot Advice section.
            </p>
          </div>
        </div>
      </div>
    </section>
  `;
}
